import type StoreService from '@ember-data/store';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import Entry from 'garaje/models/entry';
import Invite from 'garaje/models/invite';
import type Location from 'garaje/models/location';
import type UploaderFactoryService from 'garaje/services/uploader-factory';

import urlBuilder from './url-builder';

interface CreateOptions {
  location: Location;
  model: Entry | Invite;
  store: StoreService;
}

interface SaveOptions {
  blocklistFilter: BlacklistFilter;
  failOnPhotoFetchError: boolean;
  photo: File | null;
  uploaderFactory: UploaderFactoryService;
}

/**
 * Creates a new blocklist filter record from an entry or invite.
 */
export function createBlocklistFilter(options: CreateOptions): BlacklistFilter {
  const { location, model, store } = options;

  // Extract phone numbers from userData
  const phoneNumbers = extractPhoneNumber(model);
  // Extract email
  const emails = [];
  if (options.model.email) {
    emails.push(options.model.email);
  }

  // Extract photo (prefer thumbnails.large, fallback to passportAvatar)
  let photoDataUrl = null;

  if (model instanceof Invite) {
    if (model.photoUrl) {
      photoDataUrl = model.photoUrl;
    }
  }

  if (model instanceof Entry) {
    if (model.thumbnails?.original) {
      photoDataUrl = model.thumbnails.original;
    } else if (model.thumbnails?.large) {
      photoDataUrl = model.thumbnails.large;
    } else if (model.passportAvatar) {
      photoDataUrl = model.passportAvatar;
    }
  }

  // Create new blacklist filter record
  const newBlocklistFilter = store.createRecord('blacklist-filter', {
    location,
    fullName: model.fullName || '',
    emails,
    phoneNumbers,
    photo: {
      original: null,
      thumb: null,
      dataUrl: photoDataUrl,
    },
  });

  return newBlocklistFilter;
}

/**
 * Saves a blocklist filter and its photo if it has one.
 */
export async function saveBlocklistFilter(options: SaveOptions): Promise<void> {
  const { blocklistFilter, failOnPhotoFetchError, uploaderFactory } = options;
  let { photo } = options;
  const isNew = Boolean(blocklistFilter.isNew);

  if (isNew && blocklistFilter.photo?.dataUrl && !photo) {
    try {
      // cache: 'no-store' avoids a CORS failure when the browser has cached
      // a prior no-cors <img> load of the same URL (which S3 returns without
      // an Access-Control-Allow-Origin header). See SQ-7049.
      const blob = await (await fetch(<string>blocklistFilter.photo.dataUrl, { cache: 'no-store' })).blob();
      photo = new File([blob], 'photo.png', { type: 'image/png' });
    } catch (_e) {
      if (failOnPhotoFetchError) {
        throw new Error('Blocklist photo upload failed. Please try again.');
      }
    }
  }

  await blocklistFilter.save();

  if (photo) {
    const uploader = uploaderFactory.createUploader({
      url: urlBuilder.v3.blocklistFilter.photoUpload(blocklistFilter.id),
      method: 'POST',
      headers: { Accept: 'application/vnd.api+json' },
    });
    await uploader.upload(photo);
  }
}

/**
 * Extracts phone numbers from an entry or invite's userData.
 */
export function extractPhoneNumber(model: Entry | Invite): string[] {
  const phoneNumbers: string[] = [];

  // Search userData for phone number fields
  const userData = model.userData || [];
  for (const userDatum of userData) {
    const fieldLabel = userDatum.field?.toLowerCase() ?? '';
    if (fieldLabel.includes('phone') || fieldLabel === 'phonenumber' || fieldLabel === 'your phone number') {
      const phoneValue = userDatum.value;
      if (phoneValue && typeof phoneValue === 'string') {
        phoneNumbers.push(phoneValue);
      }
    }
  }

  return phoneNumbers;
}
